/* eslint-disable react/prop-types */
/**
 * ITER315 · Super-admin Security page.
 *
 * In-app self-service for the super-admin to rotate their login
 * email and password, view the audit trail, and see which recovery
 * secrets are configured on the deployment. Any successful password
 * change forces a logout because the API bumps the user's
 * `token_version`, invalidating every prior JWT — the very next
 * request from this session gets a 401 and the AuthContext falls
 * back to the login page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { KeyRound, Mail, Shield, LogOut, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminSecurity() {
  const { getHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [profile,  setProfile]  = useState(null);
  const [audit,    setAudit]    = useState([]);
  const [recovery, setRecovery] = useState(null);
  const [loading,  setLoading]  = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, a, r] = await Promise.all([
        axios.get(`${API}/api/super-admin/security/profile`, { headers: getHeaders() }),
        axios.get(`${API}/api/super-admin/security/audit`,   { headers: getHeaders() }),
        axios.get(`${API}/api/super-admin/security/recovery-config`, { headers: getHeaders() }),
      ]);
      setProfile(p.data);
      setAudit(a.data.entries || []);
      setRecovery(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load security profile');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="min-h-[300px] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading security profile…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="super-security-page">
      <header>
        <div className="flex items-center gap-2 text-slate-700">
          <Shield className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-widest">Super admin · Security</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">Credentials</h1>
        <p className="text-sm text-slate-500 mt-1">
          Rotate your login email and password. Password changes revoke every previously issued session.
        </p>
      </header>

      <ChangeEmailCard profile={profile} onSaved={loadAll} getHeaders={getHeaders} />
      <ChangePasswordCard onSaved={async () => {
        toast.success('Password changed. Please sign in again.');
        try { await logout(); } catch { /* ignore */ }
        navigate('/login', { replace: true });
      }} getHeaders={getHeaders} />
      <RecoveryConfigCard recovery={recovery} />
      <AuditTrailCard audit={audit} />
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <header className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        {Icon ? <Icon className="w-4 h-4 text-slate-700" /> : null}
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function ChangeEmailCard({ profile, onSaved, getHeaders }) {
  const [newEmail, setNewEmail]  = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !currentPw) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/super-admin/security/change-email`,
        { current_password: currentPw, new_email: newEmail.trim().toLowerCase() },
        { headers: getHeaders() });
      toast.success('Email updated.');
      setNewEmail(''); setCurrentPw('');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally { setBusy(false); }
  };
  return (
    <Card title="Change login email" icon={Mail} testid="super-security-email-card">
      <div className="text-[13px] text-slate-700 mb-3">
        Current email · <strong data-testid="super-security-current-email">{profile?.email}</strong>
      </div>
      <form onSubmit={submit} className="grid gap-3">
        <input type="email" required placeholder="New email address"
          value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
          className="h-10 border border-slate-300 rounded px-3 text-sm focus:border-slate-600 focus:outline-none"
          data-testid="super-security-new-email" />
        <input type="password" required autoComplete="current-password" placeholder="Current password (required)"
          value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
          className="h-10 border border-slate-300 rounded px-3 text-sm focus:border-slate-600 focus:outline-none"
          data-testid="super-security-email-currentpw" />
        <div className="flex justify-end">
          <button type="submit" disabled={busy}
            data-testid="super-security-email-save"
            className="h-10 px-4 rounded bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update email
          </button>
        </div>
      </form>
    </Card>
  );
}

function ChangePasswordCard({ onSaved, getHeaders }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error('New password and confirmation do not match.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/super-admin/security/change-password`,
        { current_password: currentPw, new_password: newPw, confirm_password: confirmPw },
        { headers: getHeaders() });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };
  return (
    <Card title="Change password" icon={KeyRound} testid="super-security-password-card">
      <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3 inline-flex items-center gap-2">
        <LogOut className="w-3.5 h-3.5" />
        <span>You will be signed out of every session immediately after a successful change.</span>
      </div>
      <form onSubmit={submit} className="grid gap-3">
        <input type="password" required autoComplete="current-password" placeholder="Current password"
          value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
          className="h-10 border border-slate-300 rounded px-3 text-sm focus:border-slate-600 focus:outline-none"
          data-testid="super-security-currentpw" />
        <input type="password" required autoComplete="new-password" placeholder="New password (min 8, mixed case, digit)"
          value={newPw} onChange={(e) => setNewPw(e.target.value)}
          className="h-10 border border-slate-300 rounded px-3 text-sm focus:border-slate-600 focus:outline-none"
          data-testid="super-security-newpw" />
        <input type="password" required autoComplete="new-password" placeholder="Confirm new password"
          value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
          className="h-10 border border-slate-300 rounded px-3 text-sm focus:border-slate-600 focus:outline-none"
          data-testid="super-security-confirmpw" />
        <div className="flex justify-end">
          <button type="submit" disabled={busy || !currentPw || !newPw || !confirmPw}
            data-testid="super-security-password-save"
            className="h-10 px-4 rounded bg-rose-700 hover:bg-rose-800 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Change password &amp; sign out
          </button>
        </div>
      </form>
    </Card>
  );
}

function RecoveryConfigCard({ recovery }) {
  if (!recovery) return null;
  const items = [
    { key: 'recovery_email', label: 'Recovery email address' },
    { key: 'smtp_host',      label: 'SMTP host' },
    { key: 'smtp_username',  label: 'SMTP username' },
    { key: 'smtp_password',  label: 'SMTP password' },
    { key: 'otp_secret',     label: 'OTP secret' },
  ];
  return (
    <Card title="Recovery configuration" icon={Shield} testid="super-security-recovery-card">
      <p className="text-[12px] text-slate-600 mb-3">
        Values are sourced from environment variables (Emergent Secrets) so nothing sensitive lives in the repository.
        Reset link TTL · <strong>{recovery.reset_link_ttl_minutes} min</strong>.
      </p>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.key} className="flex items-center gap-2 text-[13px]"
            data-testid={`super-security-recovery-${it.key}`}>
            {recovery[it.key]
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              : <XCircle className="w-4 h-4 text-slate-400" />}
            <span className={recovery[it.key] ? 'text-slate-800' : 'text-slate-500'}>
              {it.label} · {recovery[it.key] ? 'configured' : 'not set'}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-slate-500 mt-3">
        To configure recovery, add these keys via Profile → Emergent Secrets on this deployment:
        {' '}<code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">{(recovery.expected_env_keys || []).join(', ')}</code>
      </p>
    </Card>
  );
}

function AuditTrailCard({ audit }) {
  return (
    <Card title="Audit trail" icon={Shield} testid="super-security-audit-card">
      {audit.length === 0 && (
        <div className="text-sm text-slate-500 italic" data-testid="super-security-audit-empty">
          No security events recorded yet.
        </div>
      )}
      <ul className="space-y-1.5">
        {audit.map((row, i) => (
          <li key={i} className="text-[13px] flex items-center justify-between border-b border-slate-100 last:border-b-0 py-1"
            data-testid={`super-security-audit-row-${i}`}>
            <span className="text-slate-800">
              {row.action === 'email_changed'
                ? <>Email changed <span className="text-slate-500">from</span> <strong>{row.meta?.from}</strong> <span className="text-slate-500">to</span> <strong>{row.meta?.to}</strong></>
                : row.action === 'password_changed'
                  ? <>Password changed <span className="text-slate-500">· all sessions revoked</span></>
                  : row.action}
            </span>
            <span className="text-[11px] text-slate-500 tabular-nums">{new Date(row.at).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
